import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import { formatAnalyticalValue } from '../../utils/formatValue';
import './ResultViewers.css';

interface OilInWaterResultViewerProps {
  test: Test;
}

interface OiwParam {
  key: string;
  label: string;
  /** Set false for identity fields that must not go through the number formatter. */
  format?: boolean;
}

/**
 * The Oil in Water assay has two report formats completing the same tests:
 * the legacy WSO/OIW single-reading format (ppm) and the TD-560 two-aliquot
 * format (mg/L). Both report the result under `report_export_oiw`; the other
 * parameters are format-specific and simply absent on the other format, so a
 * single presence-filtered list renders both.
 */
const OIW_PARAMS: OiwParam[] = [
  { key: 'report_export_method', label: 'Method', format: false },
  { key: 'report_export_oiw', label: 'OIW' },
  { key: 'report_export_bkg_wso', label: 'BKG (WSO)' },
  { key: 'report_export_dispersed_oil', label: 'Dispersed Oil (OIW − WSO)' },
  { key: 'report_export_aliquot1_average', label: 'Aliquot 1 Average' },
  { key: 'report_export_aliquot2_average', label: 'Aliquot 2 Average' },
];

export const OilInWaterResultViewer: React.FC<OilInWaterResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    return OIW_PARAMS
      .filter(({ key }) => worksheetData[key] != null)
      .map(({ key, label, format }) => {
        const entry = worksheetData[key];
        const value = entry?.value ?? entry;
        const unit = (entry?.unit as string | undefined) ?? '';
        const display = format === false ? String(value ?? '') : formatAnalyticalValue(value);
        return [label, display, unit] as [string, string, string];
      });
  }, [worksheetData]);

  return (
    <div className="rv-container">
      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Raw Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported rv-section-reported-wide">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Parameter', 'Value', 'Unit']}
            rows={reportedRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {!worksheetData && !rawWorksheetData && (
        <div className="rv-empty">No Oil in Water results available for this test.</div>
      )}
    </div>
  );
};
